<template>
  <!-- Phase 1: identity still loading -->
  <template v-if="identityLoading">
    <section class="mb-4 animate-pulse">
      <div class="h-10 bg-gray-700 rounded w-48 mx-auto mb-4"></div>
      <div class="flex justify-center items-center gap-4">
        <div class="w-20 h-20 rounded-full bg-gray-700 flex-shrink-0"></div>
        <div class="ml-4 space-y-2">
          <div class="h-5 bg-gray-700 rounded w-32"></div>
          <div class="h-5 bg-gray-700 rounded w-28"></div>
          <div class="h-5 bg-gray-700 rounded w-40"></div>
          <div class="h-5 bg-gray-700 rounded w-24"></div>
        </div>
      </div>
    </section>
    <section class="mb-8 animate-pulse">
      <div class="h-7 bg-gray-700 rounded w-28 mb-2"></div>
      <div class="bg-gray-700 rounded" style="height: 380px"></div>
    </section>
  </template>

  <!-- Identity loaded -->
  <template v-else-if="identity">
    <section class="mb-4">
      <h1 class="text-center">{{ identity.username }}</h1>
      <div class="flex justify-center items-center">
        <div v-if="identity.avatar_url">
          <img
            :src="identity.avatar_url"
            :alt="identity.username"
            class="rounded-full mx-auto w-avatar-img"
          />
        </div>
        <div class="ml-4">
          <!-- Stats: loading skeleton -->
          <div v-if="statsLoading" class="animate-pulse space-y-2">
            <div class="h-5 bg-gray-700 rounded w-32"></div>
            <div class="h-5 bg-gray-700 rounded w-28"></div>
            <div class="h-5 bg-gray-700 rounded w-40"></div>
            <div class="h-5 bg-gray-700 rounded w-24"></div>
          </div>
          <div v-else-if="stats" class="statistics">
            <ul>
              <li v-for="(value, key) in stats.lifetime_statistics" :key="key">
                {{ Number(value).toLocaleString() }}
                {{ statLabel(String(key), Number(value)) }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section class="mb-8">
      <h2>Star Chart</h2>
      <div v-if="statsLoading" class="animate-pulse bg-gray-700 rounded" style="height: 380px"></div>
      <StarChart v-else-if="stats" :sessions="stats.sessions" :moon="null" />
    </section>

    <div v-if="statsError" class="text-center py-4 text-red-400">{{ statsError }}</div>
  </template>

  <!-- Identity error -->
  <div v-else-if="identityError" class="text-center py-8 text-red-400">{{ identityError }}</div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import StarChart from './StarChart.vue'
import { apiFetch } from '../utils/api'

const props = defineProps<{ slug: string }>()

interface LifetimeStatistics {
  target_count: number
  session_count: number
  image_count: number
  image_duration: number
}

interface Session {
  id: number
  session_ended_at: string
  target: { id: number; name: string; ra: number | null; dec: number | null }
  astronomer: { id: number; username: string; avatar_url: string | null; slug: string }
}

interface Identity {
  id: number
  username: string
  avatar_url: string | null
  slug: string
}

interface Stats {
  can_edit: boolean
  lifetime_statistics: LifetimeStatistics
  sessions: Session[]
}

const STAT_LABELS: Record<string, [string, string]> = {
  target_count: ['target', 'targets'],
  session_count: ['session', 'sessions'],
  image_count: ['image', 'images'],
  image_duration: ['hour', 'hours'],
}

const identity = ref<Identity | null>(null)
const identityLoading = ref(true)
const identityError = ref<string | null>(null)

const stats = ref<Stats | null>(null)
const statsLoading = ref(true)
const statsError = ref<string | null>(null)

function statLabel(key: string, value: number): string {
  const [singular, plural] = STAT_LABELS[key] ?? [key, key]
  return value === 1 ? singular : plural
}

onMounted(() => {
  apiFetch(`/api/spa/astronomers/${props.slug}`)
    .then((data) => { identity.value = data as Identity })
    .catch((e) => { identityError.value = e instanceof Error ? e.message : 'Failed to load profile' })
    .finally(() => { identityLoading.value = false })

  apiFetch(`/api/spa/astronomers/${props.slug}/stats`)
    .then((data) => { stats.value = data as Stats })
    .catch((e) => { statsError.value = e instanceof Error ? e.message : 'Failed to load stats' })
    .finally(() => { statsLoading.value = false })
})
</script>
